import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AdminMultimediaCollectionItemResponse } from '../responses/admin-multimedia-collection-item.response';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid } from '@web/utils';
import { MultimediaUpdateRequest } from '../requests/multimedia-update.request';
import { AdminMultimediaDetailsResponse } from '../responses/admin-multimedia-details.response';

@Injectable({
  providedIn: 'root',
})
export class AdminMultimediaApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<CollectionResponse<AdminMultimediaCollectionItemResponse>> {
    const path = 'admin/file/shop';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminMultimediaCollectionItemResponse,
          ),
        ),
      );
  }

  public upload(file: File): Observable<CreateResourceResponse> {
    const path = 'admin/file/shop/upload';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const form = new FormData();

    form.append('file', file);

    return this.provider.http
      .post(url, form, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)),
      );
  }

  public fetchDetailsById(
    id: Guid,
    cacheable = false,
  ): Observable<AdminMultimediaDetailsResponse> {
    const path = `admin/file/shop/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminMultimediaDetailsResponse,
          ),
        ),
      );
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/file/shop/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  public update(id: Guid, request: MultimediaUpdateRequest): Observable<void> {
    const path = `admin/file/shop/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const body = this.provider.httpHelper.requestToBody(
      request,
      MultimediaUpdateRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  protected hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Storage;
  }
}
